import React from 'react';
import PropTypes from 'prop-types';
import { Formik, useFormikContext, getIn } from 'formik';
import * as Yup from 'yup';
import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { lookupsJson } from '@ourbranch/lookups';
import { v4 as uuidv4 } from 'uuid';

import Field from 'core/components/form/form.v2';
import { SubRiskCollapsibleSection } from 'common/components/farm/locations/risks/sub-risks/sub-risk-collapsible-section';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import CollapsibleListItem from 'common/components/farm/locations/risks/sub-risks/collapsible-list-item';
import { ActionButton } from 'core/components/action-button';
import deleteIcon from 'core/assets/svg/delete-icon.svg';
import addIcon from 'core/assets/svg/ic-add.svg';
import { AppliedTag } from 'common/components/farm/applied-tag';
import useStyles from '../liability-coverages-styles';

const businessActivityOptions = lookupsJson.farm.businessActivityOptions;

const CHECKBOX_LABELS = {
  salesperson: 'Includes installation, demonstration, or service operations',
  classifiedTeachers: 'Includes corporal punishment',
  otherTeachers: 'Includes corporal punishment'
};

const TEACHER_TYPES = ['classifiedTeachers', 'otherTeachers'];

const getActivityLabel = (type) => businessActivityOptions.find((o) => o.id === type)?.value || type;

const validationSchema = Yup.object().shape({
  type: Yup.string().required('Business activity type is required'),
  description: Yup.string().required('Description is required'),
  namedInsureds: Yup.array().of(Yup.string().required('Named insured is required'))
});

const FIELD_NAME = 'farmLiabilityCoverage.businessActivities';

const BusinessActivities = ({ disabled = false, onChangeActive }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  const activities = getIn(values, FIELD_NAME) || [];

  const namedInsuredOptions = [
    ...(values.drivers || []).map((d) => ({ id: d.id, value: `${d.firstName} ${d.lastName}` })),
    ...(values.interestedParties || []).map((p) => ({ id: p.id, value: p.name }))
  ];

  return (
    <div className={classes.sectionContainer} id="farmLiabilityCoverage.businessActivities">
      <SubRiskCollapsibleSection
        type="secondary"
        content={<div className={classes.headerText}>Business activities</div>}
        contentRight={activities.length > 0 ? <AppliedTag /> : null}
        initialOpen={activities.length > 0}
      >
        <FormSectionWrapper
          sectionTitle="Scheduled items"
          totalLabel="items"
          totalCount={activities.length}
          emptyStateTitle="There are no items added yet"
          emptyStateSubtitle="To add one select from below"
        >
          {activities.map((activity, index) => {
            const checkboxLabel = CHECKBOX_LABELS[activity.type];
            const isTeacherType = TEACHER_TYPES.includes(activity.type);
            const insureds = activity.namedInsureds || [];

            return (
              <CollapsibleListItem
                key={`activity-${index}`}
                title={getActivityLabel(activity.type)}
                rightSideValues={[]}
                onDelete={() => {
                  const newActivities = activities.filter((_, i) => i !== index);
                  setFieldValue(FIELD_NAME, newActivities);
                  setFieldTouched(FIELD_NAME);
                  onChangeActive();
                }}
                disabled={disabled}
              >
                {checkboxLabel && (
                  <Field
                    xs={12}
                    type="checkbox"
                    id={`${FIELD_NAME}[${index}].includesSpecificActivities`}
                    name={`${FIELD_NAME}[${index}].includesSpecificActivities`}
                    mode="dark"
                    label={
                      isTeacherType ? (
                        <>
                          {checkboxLabel}
                          <br />
                          <span className={classes.checkboxDescription}>
                            If included, medical payments are not included for teachers.
                          </span>
                        </>
                      ) : (
                        checkboxLabel
                      )
                    }
                    disabled={disabled}
                    onChange={onChangeActive}
                  />
                )}
                <Field
                  xs={12}
                  type="textarea"
                  id={`${FIELD_NAME}[${index}].description`}
                  name={`${FIELD_NAME}[${index}].description`}
                  mode="dark"
                  label="Description"
                  disabled={disabled}
                  onChange={onChangeActive}
                />
                {insureds.map((_, nestedIndex) => (
                  <Grid container item xs={12} key={`activity-${index}-insured-${nestedIndex}`} alignItems="center">
                    <Field
                      xs={nestedIndex > 0 ? 11 : 12}
                      type="select"
                      id={`${FIELD_NAME}[${index}].namedInsureds.${nestedIndex}`}
                      name={`${FIELD_NAME}[${index}].namedInsureds.${nestedIndex}`}
                      mode="dark"
                      label={`Named insured ${String(nestedIndex + 1).padStart(2, '0')}`}
                      options={namedInsuredOptions}
                      disabled={disabled}
                      onChange={onChangeActive}
                    />
                    {nestedIndex > 0 && (
                      <Grid item xs={1}>
                        <ActionButton
                          onClick={() => {
                            const newInsureds = insureds.filter((_, i) => i !== nestedIndex);
                            setFieldValue(`${FIELD_NAME}[${index}].namedInsureds`, newInsureds);
                            onChangeActive();
                          }}
                          className={classes.deleteInsuredButton}
                          icon={deleteIcon}
                          disabled={disabled}
                          size="small"
                        />
                      </Grid>
                    )}
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Button
                    className={classes.addAnotherButton}
                    onClick={() => {
                      setFieldValue(`${FIELD_NAME}[${index}].namedInsureds`, [...insureds, '']);
                      onChangeActive();
                    }}
                    disabled={disabled}
                    startIcon={<img src={addIcon} alt="add" width={28} height={28} />}
                  >
                    Add another named insured
                  </Button>
                </Grid>
              </CollapsibleListItem>
            );
          })}

          <div className={classes.addFormContainer}>
            <Formik
              initialValues={{
                type: '',
                description: '',
                includesSpecificActivities: false,
                namedInsureds: ['']
              }}
              validateOnChange={false}
              validateOnBlur={false}
              validationSchema={validationSchema}
              onSubmit={(val, { resetForm }) => {
                const checkboxLabel = CHECKBOX_LABELS[val.type];
                const newActivity = {
                  id: uuidv4(),
                  type: val.type,
                  description: val.description,
                  ...(checkboxLabel && { includesSpecificActivities: val.includesSpecificActivities }),
                  namedInsureds: val.namedInsureds.filter(Boolean)
                };
                setFieldValue(FIELD_NAME, [...activities, newActivity]);
                setFieldTouched(FIELD_NAME);
                onChangeActive();
                resetForm();
              }}
            >
              {({ submitForm, values: formValues, setFieldValue: setInnerFieldValue }) => {
                const checkboxLabel = CHECKBOX_LABELS[formValues.type];
                const isTeacherType = TEACHER_TYPES.includes(formValues.type);

                return (
                  <Grid container spacing={2}>
                    <Grid item xs={12} container alignItems="center" wrap="nowrap">
                      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <Field
                          xs={12}
                          type="select"
                          id="type"
                          name="type"
                          mode="dark"
                          label="Type of business activity"
                          options={businessActivityOptions}
                          disabled={disabled}
                        />
                      </div>
                      {formValues.type && (
                        <ActionButton
                          onClick={() => setInnerFieldValue('type', '')}
                          className={classes.deleteInsuredButton}
                          icon={deleteIcon}
                          disabled={disabled}
                          size="small"
                        />
                      )}
                    </Grid>
                    {formValues.type && (
                      <>
                        {checkboxLabel && (
                          <Grid item key={formValues.type}>
                            <Field
                              xs={12}
                              type="checkbox"
                              id="includesSpecificActivities"
                              name="includesSpecificActivities"
                              mode="dark"
                              label={
                                isTeacherType ? (
                                  <>
                                    {checkboxLabel}
                                    <br />
                                    <span className={classes.checkboxDescription}>
                                      If included, medical payments are not included for teachers.
                                    </span>
                                  </>
                                ) : (
                                  checkboxLabel
                                )
                              }
                              disabled={disabled}
                            />
                          </Grid>
                        )}
                        <Field
                          xs={12}
                          type="textarea"
                          id="description"
                          name="description"
                          mode="dark"
                          label="Description"
                          disabled={disabled}
                        />
                        {formValues.namedInsureds.map((_, index) => (
                          <Grid item xs={12} key={`insured-${index}`} container alignItems="center" wrap="nowrap">
                            <div style={{ flexGrow: 1 }}>
                              <Field
                                xs={12}
                                type="select"
                                id={`namedInsureds.${index}`}
                                name={`namedInsureds.${index}`}
                                mode="dark"
                                label={`Named insured ${String(index + 1).padStart(2, '0')}`}
                                options={namedInsuredOptions}
                                disabled={disabled}
                              />
                            </div>
                            {index > 0 && (
                              <ActionButton
                                onClick={() => {
                                  const newInsureds = formValues.namedInsureds.filter((_, i) => i !== index);
                                  setInnerFieldValue('namedInsureds', newInsureds);
                                }}
                                className={classes.deleteInsuredButton}
                                icon={deleteIcon}
                                disabled={disabled}
                                size="small"
                              />
                            )}
                          </Grid>
                        ))}
                        <Grid item xs={12}>
                          <Button
                            className={classes.addAnotherButton}
                            onClick={() => setInnerFieldValue('namedInsureds', [...formValues.namedInsureds, ''])}
                            disabled={disabled}
                            startIcon={<img src={addIcon} alt="add" width={28} height={28} />}
                          >
                            Add another named insured
                          </Button>
                        </Grid>
                        <Grid container alignItems="center" className={classes.addButtonRow}>
                          <Button
                            type="button"
                            mode="big"
                            variant="contained"
                            color="secondary"
                            className={classes.addButton}
                            disabled={disabled}
                            onClick={submitForm}
                          >
                            Add Item
                          </Button>
                        </Grid>
                      </>
                    )}
                  </Grid>
                );
              }}
            </Formik>
          </div>
        </FormSectionWrapper>
      </SubRiskCollapsibleSection>
    </div>
  );
};

BusinessActivities.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onChangeActive: PropTypes.func.isRequired
};

export default BusinessActivities;
